import { createTheme, TypographyStyle } from "@mui/material";

import { PaletteOptions } from "@mui/material/styles";

import type {
  Background,
  Border,
  ColorVariant,
  Text,
} from "../../@types/material";
import darkTokens from "../build/tokensDark.json";
import lightTokens from "../build/tokensLight.json";

type Tokens = typeof darkTokens | typeof lightTokens;

const weightMap: { [key: string]: number } = {
  Regular: 400,
  Medium: 500,
  Semibold: 600,
  Bold: 800,
};

type FontData = {
  fontFamily: string;
  fontWeight: string;
  lineHeight: number;
  fontSize: number;
  textDecoration: string;
};

function toFont(data: FontData): TypographyStyle {
  return {
    fontFamily: data.fontFamily + ", sans-serif",
    fontWeight: weightMap[data.fontWeight],
    fontSize: data.fontSize / 16 + "rem",
    lineHeight: data.lineHeight / data.fontSize,
    letterSpacing: "-0.03em",
    textDecoration: data.textDecoration,
    textTransform: "none",
  };
}

function createBackground(tokens: Tokens, variant: ColorVariant): Background {
  return {
    soft: {
      default: tokens.Background[variant].Soft.Default.value,
      hover: tokens.Background[variant].Soft.Hover.value,
      pressed: tokens.Background[variant].Soft.Pressed.value,
    },
    moderate: {
      default: tokens.Background[variant].Moderate.Default.value,
      hover: tokens.Background[variant].Moderate.Hover.value,
      pressed: tokens.Background[variant].Moderate.Pressed.value,
    },
    strong: {
      default: tokens.Background[variant].Strong.Default.value,
      hover: tokens.Background[variant].Strong.Hover.value,
      pressed: tokens.Background[variant].Strong.Pressed.value,
    },
  };
}

function createSimplePalette(
  tokens: Tokens,
  variant: "Accent" | "Success" | "Warning" | "Danger",
  shade: "Strong" | "Moderate" | "Soft",
) {
  return {
    main: tokens.Background[variant][shade].Default.value,
    hover: tokens.Background[variant][shade].Hover.value,
    pressed: tokens.Background[variant][shade].Pressed.value,
    border: tokens.Border[variant].Default.value,
  };
}

function createBorder(tokens: Tokens, variant: ColorVariant): Border {
  return {
    strong: tokens.Border[variant].Strong.value,
    subtle: tokens.Border[variant].Subtle.value,
    default: tokens.Border[variant].Default.value,
  };
}

function createText(tokens: Tokens, variant: ColorVariant): Text {
  return {
    decoration: tokens.Text[variant].Decoration.value,
    subtle: tokens.Text[variant].Subtle.value,
    default: tokens.Text[variant].Default.value,
    contrast: tokens.Text[variant].Contrast.value,
  };
}

const baseTheme = createTheme();

function toResponsiveFont(
  variant: keyof Tokens["Heading" | "Heading-mobile"],
): TypographyStyle {
  return {
    ...toFont(lightTokens["Heading"][variant].value),
    [baseTheme.breakpoints.down("md")]: {
      ...toFont(lightTokens["Heading-mobile"][variant].value),
    },
  };
}

function toCommonFont(variant: keyof Tokens["Paragraph"]): TypographyStyle {
  return toFont(lightTokens["Paragraph"][variant].value);
}

function createPalette(tokens: Tokens): PaletteOptions {
  return {
    text: {
      primary: tokens.Text.Neutral.Default.value,
      secondary: tokens.Text.Neutral.Subtle.value,
    },
    primary: createSimplePalette(tokens, "Accent", "Strong"),
    secondary: createSimplePalette(tokens, "Accent", "Moderate"),
    success: createSimplePalette(tokens, "Success", "Moderate"),
    warning: createSimplePalette(tokens, "Warning", "Moderate"),
    error: createSimplePalette(tokens, "Danger", "Soft"),
    info: createSimplePalette(tokens, "Accent", "Soft"),
    texts: {
      success: createText(tokens, "Success"),
      warning: createText(tokens, "Warning"),
      accent: createText(tokens, "Accent"),
      neutral: createText(tokens, "Neutral"),
      danger: createText(tokens, "Danger"),
      info: createText(tokens, "Info"),
    },
    background: {
      default: tokens.Background.Static.Cold.value,
      paper: tokens.Background.Static.Neutral.value,
    },
    backgrounds: {
      success: createBackground(tokens, "Success"),
      warning: createBackground(tokens, "Warning"),
      accent: createBackground(tokens, "Accent"),
      neutral: {
        ...createBackground(tokens, "Neutral"),
        cold: {
          default: tokens.Background.Neutral.Cold.Default.value,
          hover: tokens.Background.Neutral.Cold.Hover.value,
          pressed: tokens.Background.Neutral.Cold.Pressed.value,
        },
      },
      danger: createBackground(tokens, "Danger"),
      info: createBackground(tokens, "Info"),
      input: {
        default: tokens.Background.Input.Default.value,
        hover: tokens.Background.Input.Hover.value,
        pressed: tokens.Background.Input.Pressed.value,
      },
      static: {
        neutral: tokens.Background.Static.Neutral.value,
        cold: tokens.Background.Static.Cold.value,
        soft: tokens.Background.Static.Soft.value,
        warm: tokens.Background.Static.Warm.value,
      },
    },
    divider: tokens.Border.Neutral.Subtle.value,
    borders: {
      success: createBorder(tokens, "Success"),
      warning: createBorder(tokens, "Warning"),
      accent: createBorder(tokens, "Accent"),
      neutral: createBorder(tokens, "Neutral"),
      danger: createBorder(tokens, "Danger"),
      info: createBorder(tokens, "Info"),
    },
    chart: {
      general: {
        turquoise: tokens.Chart.General.Turquoise.value,
        orange: tokens.Chart.General.Orange.value,
        mustard: tokens.Chart.General.Mustard.value,
        brown: tokens.Chart.General.Brown.value,
        darkBlue: tokens.Chart.General.Darkblue.value,
        beige: tokens.Chart.General.Beige.value,
      },
      hypnogram: {
        outOfBed: tokens.Chart.Hypnogram["Out-of-bed"].value,
        awake: tokens.Chart.Hypnogram["Awake"].value,
        REM: tokens.Chart.Hypnogram["REM"].value,
        light: tokens.Chart.Hypnogram["Light"].value,
        deep: tokens.Chart.Hypnogram["Deep"].value,
      },
    },
  };
}

export const appTheme = createTheme({
  defaultColorScheme: "light",
  cssVariables: {
    colorSchemeSelector: "class",
  },
  colorSchemes: {
    light: {
      palette: createPalette(lightTokens),
    },
    dark: {
      palette: createPalette(darkTokens),
    },
  },
  typography: {
    headingXLargeSemibold: toResponsiveFont("x-large-semibold"),
    headingLargeSemibold: toResponsiveFont("large-semibold"),
    headingLargeRegular: toResponsiveFont("large-regular"),
    headingMediumSemibold: toResponsiveFont("medium-semibold"),
    headingMediumRegular: toResponsiveFont("medium-regular"),
    headingSmallSemibold: toResponsiveFont("small-semibold"),

    bodyLargeMedium: toCommonFont("body-large-medium"),
    bodyDefaultRegular: toCommonFont("body-default-regular"),
    bodySmallRegular: toCommonFont("body-small-regular"),
    bodySmallMedium: toCommonFont("body-small-medium"),
    bodyMicroRegular: toCommonFont("body-micro-regular"),
    bodyMinimalMedium: toCommonFont("body-minimal-medium"),

    // Choose what makes the built-in forms look the best
    h1: toResponsiveFont("x-large-semibold"),
    h2: toResponsiveFont("large-semibold"),
    h3: toResponsiveFont("large-regular"),
    h4: toResponsiveFont("medium-semibold"),
    h5: toResponsiveFont("medium-regular"),
    h6: toResponsiveFont("small-semibold"),
    body1: toCommonFont("body-large-medium"),
    body2: toCommonFont("body-default-regular"),
    subtitle1: toCommonFont("body-default-regular"),
    subtitle2: toCommonFont("body-small-regular"),
    button: toCommonFont("body-small-medium"),
    caption: toCommonFont("body-micro-regular"),
    overline: toCommonFont("body-minimal-medium"),
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variant: "bodyDefaultRegular",
        component: "p",
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          // Check if color matches the TextColorVariant pattern
          const colorVariant = ownerState.color as string;
          if (colorVariant && /^[a-z]+\.([a-z]+)$/.test(colorVariant)) {
            const [variant, shade] = colorVariant.split(".") as [
              keyof typeof theme.vars.palette.texts,
              keyof Text,
            ];

            const color = theme.vars.palette.texts?.[variant]?.[shade];

            if (color) {
              return { color };
            }
          }
          return {};
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          paddingInline: 32,
          paddingBlock: 24,
        },
      },
      defaultProps: {
        variant: "headingMediumSemibold",
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
        disableElevation: true,
        disableRipple: true,
      },
      variants: [
        { props: { size: "small" }, style: { height: 36 } },
        { props: { size: "medium" }, style: { height: 36 } },
        { props: { size: "large" }, style: { height: 44 } },
        {
          props: { color: "primary", variant: "contained" },
          style: ({ theme }) => ({
            "&:hover": { backgroundColor: theme.vars.palette.primary.hover },
            "&:active": { backgroundColor: theme.vars.palette.primary.pressed },
          }),
        },
        {
          props: { color: "secondary", variant: "contained" },
          style: ({ theme }) => ({
            "&:hover": { backgroundColor: theme.vars.palette.secondary.hover },
            "&:active": {
              backgroundColor: theme.vars.palette.secondary.pressed,
            },
          }),
        },
        {
          props: { color: "success", variant: "contained" },
          style: ({ theme }) => ({
            "&:hover": { backgroundColor: theme.vars.palette.success.hover },
            "&:active": { backgroundColor: theme.vars.palette.success.pressed },
          }),
        },
        {
          props: { color: "error", variant: "contained" },
          style: ({ theme }) => ({
            borderWidth: 2,
            borderStyle: "solid",
            borderColor: theme.vars.palette.error.border,
            "&:hover": { backgroundColor: theme.vars.palette.error.hover },
            "&:active": { backgroundColor: theme.vars.palette.error.pressed },
          }),
        },
        {
          props: { color: "info", variant: "contained" },
          style: ({ theme }) => ({
            borderWidth: 2,
            borderStyle: "solid",
            borderColor: theme.vars.palette.info.border,
            "&:hover": { backgroundColor: theme.vars.palette.info.hover },
            "&:active": { backgroundColor: theme.vars.palette.info.pressed },
          }),
        },
        {
          props: { color: "warning", variant: "contained" },
          style: ({ theme }) => ({
            borderWidth: 2,
            borderStyle: "solid",
            borderColor: theme.vars.palette.warning.border,
            "&:hover": { backgroundColor: theme.vars.palette.warning.hover },
            "&:active": { backgroundColor: theme.vars.palette.warning.pressed },
          }),
        },
      ],
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        startIcon: {
          "& > svg": {
            width: 20,
            height: 20,
          },
        },
        endIcon: {
          "& > svg": {
            width: 20,
            height: 20,
          },
        },
      },
    },
    MuiIconButton: {
      variants: [
        {
          props: { size: "small" },
          style: {
            height: "28px",
            width: "28px",
            padding: "4px",
            "> *": { height: "20px", width: "20px" },
          },
        },
      ],
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          const colorVariant = ownerState.color as string;

          if (colorVariant && /^[a-z]+\.([a-z]+)$/.test(colorVariant)) {
            const [variant, shade] = colorVariant.split(".") as [
              Lowercase<ColorVariant>,
              keyof Background,
            ];

            const text = theme.vars.palette.texts?.[variant];
            const background =
              theme.vars.palette.backgrounds?.[variant]?.[shade];

            if (background && text) {
              return {
                color:
                  shade === "strong" &&
                  ["accent", "danger", "info"].includes(variant)
                    ? text.contrast
                    : text.default,
                backgroundColor: background.default,
                ":hover": {
                  backgroundColor: background.hover,
                },
                ":active": {
                  backgroundColor: background.pressed,
                },
              };
            }
          }
          return {};
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingInline: 32,
          paddingBlock: 24,
        },
      },
    },
    MuiSwitch: {
      defaultProps: {},
      styleOverrides: {
        root: {
          width: 44,
          height: 24,
          padding: 0,
        },
        switchBase: ({ theme }) => ({
          padding: 0,
          margin: "3px",
          "&.Mui-checked": {
            "& + .MuiSwitch-track": {
              backgroundColor:
                theme.vars.palette.backgrounds.info.moderate.default,
            },
          },
        }),
        thumb: ({ theme }) => ({
          width: 18,
          height: 18,
          border: `1px solid ${theme.vars.palette.borders.accent.default}`,
          backgroundColor: theme.vars.palette.backgrounds.accent.strong.default,
          boxShadow: "none",
        }),
        track: ({ theme }) => ({
          border: `2px solid ${theme.vars.palette.borders.neutral.strong}`,
          borderRadius: 24 / 2,
          backgroundColor: theme.vars.palette.backgrounds.input.default,
          opacity: 1,
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor:
            theme.vars.palette.backgrounds.accent.moderate.default,
          color: theme.vars.palette.texts.accent.default,
          borderRadius: 4,
          borderWidth: 2,
          borderStyle: "solid",
          borderColor: theme.vars.palette.info.border,
          display: "flex",
          padding: 8,
          justifyContent: "center",
          alignItems: "center",
          gap: 10,
          alignSelf: "stretch",
          ...theme.typography.bodyMicroRegular,
        }),
        arrow: ({ theme }) => ({
          color: theme.vars.palette.backgrounds.accent.moderate.default,
          //todo fix arrow size
        }),
      },
      defaultProps: {
        arrow: true,
        slotProps: {
          popper: {
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -8],
                },
              },
            ],
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          width: "100%",
        },
        ol: {
          display: "flex",
          flexWrap: "nowrap",
          width: "100%",
        },
        li: () => ({
          minWidth: 0,
          flex: "0 1 auto",
          "& .MuiTypography-root": {
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }),
      },
    },
  },
});
